import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../AppIcon';
import Button from './Button';

const MainNavigation = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeItem, setActiveItem] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const { user, userProfile, profileLoading, signOut } = useAuth();
  const autoCloseTimerRef = useRef(null);
  const menuRef = useRef(null);

  // Auto-close timer management
  const startAutoCloseTimer = () => {
    // Clear any existing timer
    if (autoCloseTimerRef?.current) {
      clearTimeout(autoCloseTimerRef?.current);
    }
    // Start new 10-second timer
    autoCloseTimerRef.current = setTimeout(() => {
      setIsExpanded(false);
    }, 10000);
  };

  const cancelAutoCloseTimer = () => {
    if (autoCloseTimerRef?.current) {
      clearTimeout(autoCloseTimerRef?.current);
      autoCloseTimerRef.current = null;
    }
  };

  // Handle menu expansion with auto-close
  const handleMenuOpen = () => {
    setIsExpanded(true);
    startAutoCloseTimer();
  };

  const handleMenuClose = () => {
    cancelAutoCloseTimer();
    setIsExpanded(false);
  };

  const handleMouseEnter = () => {
    handleMenuOpen();
  };

  const handleMouseLeave = () => {
    // Don't close immediately, let auto-close timer handle it
    // This allows the menu to stay open for 10 seconds
  };

  const handleMenuInteraction = () => {
    // Reset timer on any interaction (hover, click)
    if (isExpanded) {
      startAutoCloseTimer();
    }
  };

  // Close menu on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (isExpanded) {
        handleMenuClose();
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isExpanded]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      cancelAutoCloseTimer();
    };
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  // Update active item based on current location
  useEffect(() => {
    const currentPath = location?.pathname === '/' ? '/homepage' : location?.pathname;
    const currentHash = location?.hash;
    
    if (currentHash) {
      setActiveItem(currentPath + currentHash);
    } else {
      setActiveItem(currentPath);
    }
  }, [location]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const getNavigationItems = () => {
    if (!user) {
      return [
        { label: 'Home', path: '/homepage', icon: 'Home' },
        { label: 'Verduurzamingsoplossingen', path: '/homepage#solutions', icon: 'Zap' },
        { label: 'Kennisbank', path: '/homepage#knowledge', icon: 'BookOpen' },
        { label: 'Kosten & Terugverdientijd', path: '/costs-and-payback-period-guide', icon: 'Calculator' },
        { label: 'Subsidies & Regelingen', path: '/subsidies-and-regulations-hub', icon: 'Gift' },
        { label: 'Aanbieders', path: '/homepage#leveranciers', icon: 'Building' },
        { label: 'Reviews', path: '/homepage#reviews', icon: 'Star' },
      ];
    }

    // Wait for profile to load before showing user-specific navigation
    if (profileLoading || !userProfile) {
      return [
        { label: 'Home', path: '/homepage', icon: 'Home' },
      ];
    }

    if (userProfile?.user_type === 'customer') {
      return [
        { label: 'Home', path: '/homepage', icon: 'Home' },
        { label: 'Offerte Aanvragen', path: '/quote-request-form', icon: 'FileText' },
        { label: 'Mijn Projecten', path: '/my-projects', icon: 'FolderOpen' },
        { label: 'Aanbieders Zoeken', path: '/find-leveranciers', icon: 'Search' },
        { label: 'Profiel', path: '/customer-profile', icon: 'User' },
      ];
    }

    // Sales Agent navigation
    if (userProfile?.user_type === 'sales_agent') {
      return [
        { label: 'Dashboard', path: '/sales-agent-dashboard', icon: 'LayoutDashboard' },
        { label: 'CRM Systeem', path: '/sales-agent-crm', icon: 'Briefcase' },
        { label: 'Klanten', path: '/sales-agent-dashboard', icon: 'Users' },
        { label: 'Leads', path: '/sales-agent-dashboard', icon: 'Target' },
        { label: 'Prestaties', path: '/sales-agent-dashboard', icon: 'TrendingUp' },
      ];
    }

    // Reseller navigation
    if (userProfile?.user_type === 'reseller') {
      return [
        { label: 'Dashboard', path: '/reseller-dashboard', icon: 'LayoutDashboard' },
        { label: 'CRM Systeem', path: '/reseller-crm', icon: 'Briefcase' },
        { label: 'Klanten', path: '/reseller-dashboard', icon: 'Users' },
        { label: 'Partnerschappen', path: '/reseller-dashboard', icon: 'Handshake' },
        { label: 'Omzet', path: '/reseller-dashboard', icon: 'DollarSign' },
      ];
    }

    // Provider, Supplier - all have CRM access
    if (['provider', 'supplier']?.includes(userProfile?.user_type)) {
      return [
        { label: 'Dashboard', path: '/leverancier-dashboard', icon: 'LayoutDashboard' },
        { label: 'Producten', path: '/provider-product-management', icon: 'Package' },
        { label: 'Reviews', path: '/provider-review-management', icon: 'MessageSquare' },
        { label: 'Nieuws', path: '/provider-news-updates-center', icon: 'Newspaper' },
        { label: 'CRM Systeem', path: '/customer-relationship-manager', icon: 'Briefcase' },
        { label: 'CRM Dashboard', path: '/crm-dashboard-overview', icon: 'BarChart3' },
        { label: 'Offertebeheer', path: '/quote-management-system', icon: 'FileText' },
        { label: 'Offerteaanvragen', path: '/leverancier-dashboard#quotes', icon: 'Inbox' },
        { label: 'Mijn Diensten', path: '/leverancier-services', icon: 'Settings' },
        { label: 'Profiel', path: '/leverancier-profile', icon: 'Building' },
      ];
    }

    if (userProfile?.user_type === 'superadmin') {
      return [
        { label: 'Home', path: '/homepage', icon: 'Home' },
        { label: 'CRM Dashboard', path: '/crm-dashboard-overview', icon: 'BarChart3' },
        { label: 'Offertebeheer', path: '/quote-management-system', icon: 'FileText' },
        { label: 'Klantenbeheer', path: '/customer-relationship-manager', icon: 'Users' },
        { label: 'Aanbieders Overzicht', path: '/leveranciers', icon: 'Building' },
        { label: 'Kennisbank', path: '/sustainability-knowledge-hub', icon: 'BookOpen' },
      ];
    }

    // Fallback for unknown user types
    return [
      { label: 'Home', path: '/homepage', icon: 'Home' },
    ];
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/homepage');
  };

  const getUserTypeLabel = () => {
    const labels = {
      customer: 'Klant',
      provider: 'Leverancier',
      supplier: 'Leverancier',
      sales_agent: 'Sales Agent',
      reseller: 'Reseller',
      superadmin: 'Beheerder',
    };
    return labels?.[userProfile?.user_type] || 'Gebruiker';
  };

  const navigationItems = getNavigationItems();
  const isActivePath = (path) => {
    // Normalize current path: treat '/' as '/homepage'
    const currentPath = location?.pathname === '/' ? '/homepage' : location?.pathname;
    
    if (path?.includes('#')) {
      // For hash links, check if this specific hash link is active
      return activeItem === path;
    }
    
    // For regular paths, match the pathname exactly
    // Also ensure we don't highlight home when on other pages
    return currentPath === path && !location?.hash;
  };

  const handleNavClick = (path) => {
    if (path?.includes('#')) {
      const [pathname, hash] = path?.split('#');
      const normalizedCurrentPath = location?.pathname === '/' ? '/homepage' : location?.pathname;
      
      if (normalizedCurrentPath === pathname) {
        // Same page, just scroll to section
        const element = document.getElementById(hash);
        if (element) {
          element?.scrollIntoView({ behavior: 'smooth' });
        }
      } else {
        // Different page, navigate with hash
        navigate(path);
      }
      
      // Update active item immediately for visual feedback
      setActiveItem(path);
    } else {
      // Regular navigation, clear hash from active item
      setActiveItem(path);
    }
    
    // Close menu after navigation
    handleMenuClose();
  };

  return (
    <>
      {/* Desktop Vertical Sidebar - Completely Hidden When Collapsed */}
      <aside
        ref={menuRef}
        className="hidden lg:block fixed top-0 left-0 h-screen z-50"
        onMouseEnter={() => {
          if (isExpanded) {
            handleMenuInteraction();
          }
        }}
        onMouseLeave={handleMouseLeave}
        style={{
          width: isExpanded ? '280px' : 'auto',
          transition: 'width 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        {/* Collapsed State - Only Leaf Icon + "menu" text (No vertical bar) */}
        {!isExpanded && (
          <div className="absolute top-4 left-4 z-50">
            <button
              onClick={handleMenuOpen}
              onMouseEnter={handleMouseEnter}
              className="flex flex-col items-center justify-center space-y-1 transition-all hover:opacity-80 cursor-pointer group"
              aria-label="Open menu"
            >
              <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center shadow-elevation-2 flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                <Icon name="Leaf" size={28} color="white" />
              </div>
              <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider group-hover:text-foreground transition-colors">
                menu
              </span>
            </button>
          </div>
        )}

        {/* Expanded State - Full Vertical Bar Appears */}
        {isExpanded && (
          <div 
            className="h-full bg-card border-r border-border shadow-elevation-2 flex flex-col overflow-hidden"
            style={{
              width: '280px',
              animation: 'slideInFromLeft 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
              transformOrigin: 'top left',
            }}
          >
            {/* Logo Section - Animated */}
            <div 
              className="p-4 border-b border-border flex items-center justify-center"
              style={{
                animation: 'slideInFromLeft 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              <Link
                to="/homepage"
                className="flex items-center space-x-3 transition-opacity hover:opacity-80"
                onClick={() => handleMenuClose()}
              >
                <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center shadow-elevation-1 flex-shrink-0">
                  <Icon name="Leaf" size={24} color="white" />
                </div>
                <span className="font-heading font-bold text-lg text-foreground whitespace-nowrap">
                  Verduurzaamwijzer
                </span>
              </Link>
            </div>

            {/* User Info (if logged in) - Animated */}
            {user && (
              <div 
                className="p-4 border-b border-border"
                style={{
                  animation: 'slideInFromLeft 0.7s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              >
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                    <Icon
                      name={userProfile?.user_type === 'provider' ? 'Building' : 'User'}
                      size={20}
                      color="white"
                    />
                  </div>
                  <div className="overflow-hidden">
                    <p className="font-medium text-sm text-foreground truncate">
                      {userProfile?.full_name || 'Gebruiker'}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Items - Staggered Animation */}
            <nav className="flex-1 overflow-y-auto p-3 space-y-1">
              {navigationItems?.map((item, index) => (
                <Link
                  key={item?.path}
                  to={item?.path}
                  onClick={(e) => {
                    if (item?.path?.includes('#')) {
                      e?.preventDefault();
                      handleNavClick(item?.path);
                    } else {
                      handleNavClick(item?.path);
                    }
                  }}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg text-sm font-body font-medium transition-all duration-200 group ${
                    isActivePath(item?.path)
                      ? 'bg-primary text-primary-foreground shadow-elevation-1'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                  style={{
                    animation: `slideInFromLeft ${0.4 + index * 0.05}s cubic-bezier(0.4, 0, 0.2, 1)`,
                  }}
                >
                  <Icon name={item?.icon} size={20} className="flex-shrink-0" />
                  <span className="whitespace-nowrap">{item?.label}</span>
                </Link>
              ))}
            </nav>

            {/* Auth Actions - Animated */}
            <div 
              className="p-3 border-t border-border space-y-2"
              style={{
                animation: 'slideInFromLeft 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              {!user ? (
                <>
                  <Link to="/customer-login" onClick={() => handleMenuClose()}>
                    <Button
                      variant="default"
                      size="sm"
                      fullWidth
                      className="justify-start transition-all"
                    >
                      <Icon name="LogIn" size={18} className="flex-shrink-0" />
                      <span className="ml-2">Inloggen</span>
                    </Button>
                  </Link>
                  <Link to="/customer-registration" onClick={() => handleMenuClose()}>
                    <Button
                      variant="outline"
                      size="sm"
                      fullWidth
                      className="justify-start transition-all"
                    >
                      <Icon name="UserPlus" size={18} className="flex-shrink-0" />
                      <span className="ml-2">Registreren</span>
                    </Button>
                  </Link>
                  <Link to="/leverancier-login" onClick={() => handleMenuClose()}>
                    <Button
                      variant="outline"
                      size="sm"
                      fullWidth
                      className="justify-start transition-all"
                    >
                      <Icon name="Building" size={18} className="flex-shrink-0" />
                      <span className="ml-2">Leverancier</span>
                    </Button>
                  </Link>
                  <Link to="/sales-agent-login" onClick={() => handleMenuClose()}>
                    <Button
                      variant="outline"
                      size="sm"
                      fullWidth
                      className="justify-start transition-all"
                    >
                      <Icon name="UserCheck" size={18} className="flex-shrink-0" />
                      <span className="ml-2">Sales Agent</span>
                    </Button>
                  </Link>
                  <Link to="/reseller-login" onClick={() => handleMenuClose()}>
                    <Button
                      variant="outline"
                      size="sm"
                      fullWidth
                      className="justify-start transition-all"
                    >
                      <Icon name="Briefcase" size={18} className="flex-shrink-0" />
                      <span className="ml-2">Reseller</span>
                    </Button>
                  </Link>
                </>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  fullWidth
                  onClick={handleLogout}
                  className="justify-start transition-all text-destructive hover:text-destructive hover:bg-destructive/10"
                >
                  <Icon name="LogOut" size={18} className="flex-shrink-0" />
                  <span className="ml-2">Uitloggen</span>
                </Button>
              )}
            </div>
          </div>
        )}
      </aside>

      {/* Mobile Top Bar */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-card border-b border-border shadow-elevation-1">
        <div className="flex items-center justify-between h-16 px-4">
          {/* Logo */}
          <Link
            to="/homepage"
            className="flex items-center space-x-2 transition-opacity hover:opacity-80"
          >
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center shadow-elevation-1">
              <Icon name="Leaf" size={18} color="white" />
            </div>
            <span className="font-heading font-bold text-base text-foreground">
              Verduurzaamwijzer
            </span>
          </Link>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-all"
            aria-label="Toggle menu"
            aria-expanded={isMobileMenuOpen}
          >
            <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={24} />
          </button>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden animate-fade-in"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Menu Drawer (from left) */}
      <div
        className={`fixed top-0 left-0 bottom-0 w-80 max-w-[85vw] bg-card z-50 lg:hidden transform transition-transform duration-300 ease-out shadow-elevation-3 ${
          isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Mobile Menu Header */}
          <div className="flex items-center justify-between p-4 border-b border-border">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <Icon name="Leaf" size={18} color="white" />
              </div>
              <span className="font-heading font-bold text-lg text-foreground">
                Verduurzaamwijzer
              </span>
            </div>
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-all"
              aria-label="Close menu"
            >
              <Icon name="X" size={24} />
            </button>
          </div>

          {/* Mobile Menu Content */}
          <div className="flex-1 overflow-y-auto p-4">
            {/* User Info (if logged in) */}
            {user && (
              <div className="mb-6 p-4 bg-muted rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center">
                    <Icon
                      name={userProfile?.user_type === 'provider' ? 'Building' : 'User'}
                      size={24}
                      color="white"
                    />
                  </div>
                  <div>
                    <p className="font-medium text-foreground">
                      {userProfile?.full_name || 'Gebruiker'}
                    </p>
                    <p className="text-sm text-muted-foreground">{user?.email}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Items */}
            <nav className="space-y-1 mb-6">
              {navigationItems?.map((item) => (
                <Link
                  key={item?.path}
                  to={item?.path}
                  onClick={(e) => {
                    if (item?.path?.includes('#')) {
                      e?.preventDefault();
                      handleNavClick(item?.path);
                    } else {
                      handleNavClick(item?.path);
                    }
                    setIsMobileMenuOpen(false);
                  }}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg text-base font-body font-medium transition-all duration-200 ${
                    isActivePath(item?.path)
                      ? 'bg-primary text-primary-foreground shadow-elevation-1'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name={item?.icon} size={22} />
                  <span>{item?.label}</span>
                </Link>
              ))}
            </nav>

            {/* Mobile Auth Actions */}
            <div className="space-y-2 pt-4 border-t border-border">
              {!user ? (
                <>
                  <Link to="/customer-login" className="block" onClick={() => setIsMobileMenuOpen(false)}>
                    <Button variant="default" size="lg" fullWidth className="justify-center">
                      <Icon name="LogIn" size={18} className="mr-2" />
                      Inloggen
                    </Button>
                  </Link>
                  <Link to="/customer-registration" className="block" onClick={() => setIsMobileMenuOpen(false)}>
                    <Button variant="outline" size="lg" fullWidth className="justify-center">
                      Gratis Registreren
                    </Button>
                  </Link>
                  <Link to="/leverancier-login" className="block" onClick={() => setIsMobileMenuOpen(false)}>
                    <Button variant="ghost" size="lg" fullWidth className="justify-center">
                      <Icon name="Building" size={18} className="mr-2" />
                      Voor Leverancier
                    </Button>
                  </Link>
                  <Link to="/sales-agent-login" className="block" onClick={() => setIsMobileMenuOpen(false)}>
                    <Button variant="ghost" size="lg" fullWidth className="justify-center">
                      <Icon name="UserCheck" size={18} className="mr-2" />
                      Voor Sales Agent
                    </Button>
                  </Link>
                  <Link to="/reseller-login" className="block" onClick={() => setIsMobileMenuOpen(false)}>
                    <Button variant="ghost" size="lg" fullWidth className="justify-center">
                      <Icon name="Briefcase" size={18} className="mr-2" />
                      Voor Reseller
                    </Button>
                  </Link>
                </>
              ) : (
                <Button
                  variant="destructive"
                  size="lg"
                  fullWidth
                  onClick={handleLogout}
                  className="justify-center"
                >
                  <Icon name="LogOut" size={18} className="mr-2" />
                  Uitloggen
                </Button>
              )}
            </div>
          </div>

          {/* Mobile Menu Footer */}
          <div className="p-4 border-t border-border bg-muted/30">
            <p className="text-xs text-center text-muted-foreground">
              © 2026 Verduurzaamwijzer. Onafhankelijk & transparant.
            </p>
          </div>
        </div>
      </div>

      {/* CSS Keyframes for animations */}
      <style jsx>{`
        @keyframes slideInFromLeft {
          from {
            opacity: 0;
            transform: translateX(-30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
      `}</style>
    </>
  );
};

export default MainNavigation;